import { forwardRef, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { cn } from '@/utils'
import { X } from 'lucide-react'

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}

export function Modal({ open, onOpenChange, children }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </Dialog.Root>
  )
}

interface ModalTriggerProps {
  children: ReactNode
  asChild?: boolean
}

export function ModalTrigger({ children, asChild }: ModalTriggerProps) {
  return <Dialog.Trigger asChild={asChild}>{children}</Dialog.Trigger>
}

interface ModalContentProps {
  children: ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showClose?: boolean
  /** Forwarded to Radix Dialog.Content. Call e.preventDefault() to disable Escape-to-close. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void
  /** Forwarded to Radix Dialog.Content. Call e.preventDefault() to disable click-outside-to-close. */
  onPointerDownOutside?: (event: Event) => void
  /** Forwarded to Radix Dialog.Content. Call e.preventDefault() to disable any interaction outside the modal. */
  onInteractOutside?: (event: Event) => void
}

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  (
    {
      children,
      className,
      size = 'md',
      showClose = true,
      onEscapeKeyDown,
      onPointerDownOutside,
      onInteractOutside,
    },
    ref,
  ) => {
    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-lg',
      lg: 'max-w-2xl',
      xl: 'max-w-4xl',
      full: 'max-w-[90vw]',
    }

    return (
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=open]:animate-fade-in" />
        <Dialog.Content
          ref={ref}
          aria-describedby={undefined}
          onEscapeKeyDown={onEscapeKeyDown}
          onPointerDownOutside={onPointerDownOutside}
          onInteractOutside={onInteractOutside}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-full',
            sizeClasses[size],
            'max-h-[90vh] overflow-hidden',
            'bg-background',
            'rounded-xl shadow-2xl',
            'border border-border',
            'data-[state=open]:animate-slide-up',
            'focus:outline-hidden',
            className
          )}
        >
          {children}
          {showClose && (
            <Dialog.Close asChild>
              <button
                className="absolute right-4 top-4 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    )
  }
)

ModalContent.displayName = 'ModalContent'

interface ModalHeaderProps {
  children: ReactNode
  className?: string
}

export function ModalHeader({ children, className }: ModalHeaderProps) {
  return (
    <div
      className={cn(
        'px-6 py-4 border-b border-border',
        className
      )}
    >
      {children}
    </div>
  )
}

interface ModalTitleProps {
  children: ReactNode
  className?: string
}

export function ModalTitle({ children, className }: ModalTitleProps) {
  return (
    <Dialog.Title
      className={cn(
        'text-lg font-semibold text-foreground',
        className
      )}
    >
      {children}
    </Dialog.Title>
  )
}

interface ModalDescriptionProps {
  children: ReactNode
  className?: string
}

export function ModalDescription({ children, className }: ModalDescriptionProps) {
  return (
    <Dialog.Description
      className={cn('text-sm text-muted-foreground mt-1', className)}
    >
      {children}
    </Dialog.Description>
  )
}

interface ModalBodyProps {
  children: ReactNode
  className?: string
}

export function ModalBody({ children, className }: ModalBodyProps) {
  return (
    <div
      className={cn(
        'px-6 py-4 overflow-y-auto',
        'max-h-[calc(90vh-12rem)]',
        className
      )}
    >
      {children}
    </div>
  )
}

interface ModalFooterProps {
  children: ReactNode
  className?: string
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div
      className={cn(
        'px-6 py-4 border-t border-border',
        'flex items-center justify-end gap-3',
        className
      )}
    >
      {children}
    </div>
  )
}
